import { useEffect, useMemo, useState } from "react";
import Modal from "@modalSrc/Modal";
import Request from "@elements/Request";
import TestDetailsModal from "@modals/TestDetailsModal";
import ReusableCodeResult from "@modals/ReusableCodeResult";
import { useI18n } from "@/i18n/I18nProvider";
import { readPermIdsFromSession, safeReadCurrentUserFromSession } from "@/i18n/sessionUser";

type RequestModalProps = {
  isOpen: boolean;
  title?: string;
  className?: string;
  onClose?: () => void;
  companyId?: number | string;
  company?: unknown;
  updateSpinner?: (isLoading: boolean) => void;
  onCreated?: (testState?: unknown) => void;
  cttId?: number | string;
  /** When set with cttId (e.g. create-from-CTT-row), shown in the modal caption for context. */
  cttName?: string;
  /** Preselect the active company test type with this eval comparison when the modal opens. */
  preferredEvalComparison?: number;
  /** Eval comparisons omitted from the test-type dropdown, such as BE Source on Create Questionnaire. */
  excludeEvalComparisons?: number[];
  /** When set, the test-type dropdown includes only these eval comparisons. BE is `EF`. */
  includeEvalComparisons?: number[];
  /** Site already chosen by the caller. Hidden on the form and submitted with the test. */
  lockedSiteId?: number | null;
  /** Job already chosen by the caller. Hidden on the form and submitted with the test. */
  lockedJobId?: number | null;
  adminPerms?: unknown;
};

type CreatedTestState = {
  id?: number | string;
  testInstanceId?: number | string;
  reusableCode?: string;
  testData?: {
    id?: number | string;
    testInstanceId?: number | string;
  };
};

export default function RequestModal({
  isOpen,
  title = "Create Test",
  className,
  onClose,
  companyId,
  company,
  updateSpinner,
  onCreated,
  cttId,
  cttName,
  preferredEvalComparison,
  excludeEvalComparisons,
  includeEvalComparisons,
  lockedSiteId,
  lockedJobId,
  adminPerms,
}: RequestModalProps) {
  const [createdTestId, setCreatedTestId] = useState<number | string | null>(null);
  const [createdTestInstanceId, setCreatedTestInstanceId] = useState<number | string | null>(null);
  const [reusableCode, setReusableCode] = useState<string | null>(null);
  const [requestModalTitleOverride, setRequestModalTitleOverride] = useState("");
  const currentUser = safeReadCurrentUserFromSession();
  const { t, languageId: currentLanguageId } = useI18n();

  const localizedTitle = useMemo(() => {
    const titleOverride = String(requestModalTitleOverride || "").trim();
    const canUseTitleOverride = String(title || "").trim().toLowerCase() === "create test";
    let sourceTitle = (canUseTitleOverride && titleOverride ? titleOverride : String(title || "Create Test")).trim();
    const cttLabel = String(cttName || "").trim();
    if (cttId && cttLabel) {
      sourceTitle = `${sourceTitle} — ${cttLabel}`;
    }
    if (!sourceTitle) return "Create Test";
    if (!currentUser?.userId || currentLanguageId <= 1) return sourceTitle;

    return t(sourceTitle, sourceTitle);
  }, [title, requestModalTitleOverride, cttId, cttName, currentUser?.userId, currentLanguageId, t]);

  const isSystemAdmin = useMemo(() => {
    const systemAdminPerm = 254;

    const hasSystemAdminPerm = (perms: unknown) => {
      if (!Array.isArray(perms)) return false;
      return perms.some((permission: unknown) => {
        if (permission == null) return false;
        if (typeof permission === "number" || typeof permission === "string") {
          return Number(permission) === systemAdminPerm;
        }
        if (typeof permission === "object") {
          const record = permission as Record<string, unknown>;
          const candidates = [
            record.id,
            record.permId,
            record.permissionId,
            record.value,
          ];
          return candidates.some((candidate) => Number(candidate) === systemAdminPerm);
        }
        return false;
      });
    };

    if (hasSystemAdminPerm(adminPerms)) return true;
    if (hasSystemAdminPerm(currentUser?.permissions)) return true;
    if (readPermIdsFromSession().includes(systemAdminPerm)) return true;

    return false;
  }, [adminPerms, currentUser?.permissions, currentUser?.permIds]);

  const handleCreated = (testState: CreatedTestState | undefined) => {
    const sharedCode = String(testState?.reusableCode || "").trim();
    const testId = testState?.testData?.id || testState?.id;
    const testInstanceId = testState?.testData?.testInstanceId || testState?.testInstanceId;
    if (sharedCode && !testId) {
      setReusableCode(sharedCode);
      return;
    }
    if (testId) {
      setCreatedTestId(testId);
      setCreatedTestInstanceId(testInstanceId);
    } else {
      if (onCreated) onCreated(testState);
      if (onClose) onClose();
    }
  };

  const handleReusableCodeClose = () => {
    setReusableCode(null);
    if (onCreated) onCreated();
    if (onClose) onClose();
  };

  const handleTestDetailsClose = () => {
    setCreatedTestId(null);
    setCreatedTestInstanceId(null);
    if (onCreated) onCreated();
    if (onClose) onClose();
  };

  const handleRequestModalClose = () => {
    setCreatedTestId(null);
    setCreatedTestInstanceId(null);
    setReusableCode(null);
    setRequestModalTitleOverride("");
    if (onClose) onClose();
  };

  return (
    <>
      <Modal
        isOpen={!!isOpen && !createdTestId && !reusableCode}
        title={localizedTitle}
        onClose={handleRequestModalClose}
        className={`requestModal${className ? ` ${className}` : ""}`}
        size="lg"
      >
        <div className="requestModalBody">
          <Request
            key={isOpen && cttId ? `create-${cttId}-${lockedSiteId ?? 0}-${lockedJobId ?? 0}` : "create-closed"}
            active={!!isOpen}
            companyId={companyId}
            company={company}
            updateSpinner={updateSpinner}
            onCreate={handleCreated}
            onClose={handleRequestModalClose}
            cttId={cttId}
            preferredEvalComparison={preferredEvalComparison}
            excludeEvalComparisons={excludeEvalComparisons}
            includeEvalComparisons={includeEvalComparisons}
            lockedSiteId={lockedSiteId}
            lockedJobId={lockedJobId}
            onRequestModalTitleTextChange={setRequestModalTitleOverride}
            />
        </div>
      </Modal>
      
      {reusableCode && (
        <Modal
          isOpen
          title="Test link"
          onClose={handleReusableCodeClose}
          className="requestModal reusableCodeModal"
          size="lg"
        >
          <ReusableCodeResult code={reusableCode} onClose={handleReusableCodeClose} />
        </Modal>
      )}

      {createdTestId && (
        <TestDetailsModal
          testId={createdTestId}
          testInstanceId={createdTestInstanceId}
          onClose={handleTestDetailsClose}
          updateSpinner={updateSpinner}
          isSystemAdmin={isSystemAdmin}
          adminPerms={adminPerms}
        />
      )}
    </>
  );
}


